import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Music, Mail, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import * as api from '@/lib/api';

const ForgotPassword = () => {
  const [emailOrUsername, setEmailOrUsername] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [resetToken, setResetToken] = useState<string | null>(null);
  const navigate = useNavigate();
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const trimmedInput = emailOrUsername.trim();

    if (!trimmedInput) {
      toast({
        title: "Email or username required",
        description: "Please enter your email address or username.",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);

    try {
      const token = await api.userAPI.requestPasswordReset(trimmedInput);
      setResetToken(token);
      toast({
        title: "Reset token generated",
        description: "Please copy the reset token below and use it to reset your password.",
      });
    } catch (error) {
      console.error('Password reset request error:', error);
      const message =
        error instanceof Error ? error.message : 'Unable to request password reset. Please try again.';
      toast({
        title: "Request failed",
        description: message,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-8 bg-background">
      <div className="w-full max-w-md space-y-8">
        {/* Logo */}
        <div className="text-center">
          <Link to="/" className="inline-flex items-center space-x-2 mb-8">
            <div className="w-10 h-10 rounded-lg bg-music-gradient flex items-center justify-center">
              <Music className="h-6 w-6 text-white" />
            </div>
            <span className="text-2xl font-bold text-foreground">BeatBattle</span>
          </Link>
        </div>

        <Card className="card-shadow border-2 border-primary/30 bg-gradient-to-br from-purple-100/40 to-blue-100/40 dark:from-purple-950/20 dark:to-blue-950/20 shadow-md">
          <CardHeader className="text-center bg-gradient-to-r from-primary/15 to-blue-500/15 dark:from-primary/20 dark:to-blue-400/20 rounded-t-lg border-b border-primary/30">
            <CardTitle className="text-2xl font-bold text-primary">Reset Password</CardTitle>
            <p className="text-muted-foreground">
              Enter your email or username to receive a reset token
            </p>
          </CardHeader>
          <CardContent>
            {!resetToken ? (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="emailOrUsername">Email or Username</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="emailOrUsername"
                      type="text"
                      placeholder="Enter your email or username"
                      className="pl-10"
                      value={emailOrUsername}
                      onChange={(e) => setEmailOrUsername(e.target.value)}
                      required
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  className="w-full btn-music"
                  disabled={isLoading}
                >
                  {isLoading ? 'Generating token...' : 'Request Reset Token'}
                </Button>

                <div className="text-center">
                  <Link
                    to="/login"
                    className="inline-flex items-center text-sm text-primary hover:underline"
                  >
                    <ArrowLeft className="h-4 w-4 mr-1" />
                    Back to login
                  </Link>
                </div>
              </form>
            ) : (
              <div className="space-y-6">
                <div className="p-4 bg-muted rounded-lg">
                  <p className="text-sm text-muted-foreground mb-2">
                    Your password reset token:
                  </p>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 p-2 bg-background rounded border text-sm font-mono break-all">
                      {resetToken}
                    </code>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        navigator.clipboard.writeText(resetToken);
                        toast({
                          title: "Token copied",
                          description: "Reset token copied to clipboard.",
                        });
                      }}
                    >
                      Copy
                    </Button>
                  </div>
                </div>

                <p className="text-sm text-muted-foreground text-center">
                  Copy this token and use it on the reset password page to set a new password.
                </p>

                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    className="flex-1"
                    onClick={() => {
                      setResetToken(null);
                      setEmailOrUsername('');
                    }}
                  >
                    Request Another Token
                  </Button>
                  <Button
                    type="button"
                    className="flex-1 btn-music"
                    onClick={() => navigate(`/reset-password?token=${resetToken}`)}
                  >
                    Go to Reset Page
                  </Button>
                </div>

                <div className="text-center">
                  <Link
                    to="/login"
                    className="inline-flex items-center text-sm text-primary hover:underline"
                  >
                    <ArrowLeft className="h-4 w-4 mr-1" />
                    Back to login
                  </Link>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ForgotPassword;

